import React, { useMemo, useState } from 'react'
import LiveModal from 'live/components/ui/Modal'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'
import i18n from 'live/services/i18n'
import { ValuePropsDiv, ValueSetup } from './ValueSetup'
import { VALUE_OPTIONS_DEFAULT_STATE } from '../constants'
import { LayerConfigInterface, ValueOptionsInterface, TypeVariableOptions } from '../types'
import { IconChooser } from './IconChooser/IconChooser'

type mainModalProps = {
    layerKey: string
    layerConfig: LayerConfigInterface
    isVisible: boolean
    setIsVisible: (value: boolean) => void
    changeLayerConfig: (key: string, newLayerConfig: LayerConfigInterface) => void
    setLayerSelected: (any) => void
    variableOptions: TypeVariableOptions
}

export const MainModal = ({
    layerKey,
    layerConfig,
    isVisible,
    setIsVisible,
    changeLayerConfig,
    setLayerSelected,
    variableOptions
}: mainModalProps) => {
    const [editLayerConfig, setEditLayerConfig] = useState(layerConfig)
    const [mainValue, setMainValue] = useState(layerConfig.mainValue)
    const [secondaryValues, setSecondaryValues] = useState(layerConfig.secondaryValues)
    const [isIconChoserOpen, setIsIconChoserOpen] = useState(false)
    const [saveIconAux, setSaveIconAux] = useState<number>(null)

    const addSecondaryValue = () => {
        const newSecondaryValues = [...secondaryValues, VALUE_OPTIONS_DEFAULT_STATE]
        setSecondaryValues(newSecondaryValues)
        setEditLayerConfig({ ...editLayerConfig, secondaryValues: newSecondaryValues })
    }

    const removeSecondaryValue = (index: number) => {
        const newSecondaryValues = Array.from(secondaryValues)
        newSecondaryValues.splice(index, 1)
        setSecondaryValues(newSecondaryValues)
        setEditLayerConfig({ ...editLayerConfig, secondaryValues: newSecondaryValues })
    }

    const changeValueOptions = (
        valueOptions: ValueOptionsInterface,
        configItemType: string,
        value: any,
        itemKey: string,
        indexSecondaryValue?: number
    ): void => {
        let newEditLayerConfig = { ...editLayerConfig }
        let newValueOptions = {
            ...valueOptions,
            [configItemType]: { ...valueOptions[configItemType], [itemKey]: value }
        }
        if (indexSecondaryValue === undefined) {
            newEditLayerConfig = { ...editLayerConfig, mainValue: { ...newValueOptions } }
            setEditLayerConfig(newEditLayerConfig)
            setMainValue(newValueOptions)
        } else {
            secondaryValues[indexSecondaryValue] = newValueOptions
            newEditLayerConfig = { ...editLayerConfig, secondaryValues: secondaryValues }
            setEditLayerConfig(newEditLayerConfig)
            setSecondaryValues(secondaryValues)
        }
    }

    const onCancel = (): void => {
        setLayerSelected(null)
        setIsVisible(false)
    }

    const onSave = (): void => {
        changeLayerConfig(layerKey, editLayerConfig)
        setSaveIconAux(null)
    }

    const onSubmit = (): void => {
        onSave()
        setLayerSelected(null)
        setIsVisible(false)
    }

    const handleSaveIcon = (icon: string) => {
        if (saveIconAux === undefined) {
            changeValueOptions(mainValue, 'label', icon, 'iconValue')
        } else {
            changeValueOptions(secondaryValues[saveIconAux], 'label', icon, 'iconValue', saveIconAux)
        }
    }

    const getChoosedIcon = () => {
        let choosedIcon = null
        if (saveIconAux === undefined) {
            choosedIcon = mainValue?.label?.iconValue
        } else {
            choosedIcon = secondaryValues[saveIconAux]?.label?.iconValue
        }

        return choosedIcon
    }

    return useMemo(
        () => (
            <StyledModal show={isVisible}>
                <Header>
                    <span>{layerConfig.name + ' ' + i18n('Style')}</span>
                </Header>

                <Body>
                    <Title>{i18n('MAIN VALUE')}</Title>
                    <ValueHeader />
                    <ValueSetup
                        valueOptions={mainValue}
                        isMainValue={true}
                        changeValueOptions={changeValueOptions}
                        variableOptions={variableOptions}
                        setIsIconChoserOpen={setIsIconChoserOpen}
                        setSaveIconAux={setSaveIconAux}
                    />

                    <DivLine />

                    <Title>{i18n('SECONDARY VALUES')}</Title>
                    {secondaryValues.length > 0 && <ValueHeader />}

                    {secondaryValues.map((value, index) => {
                        return (
                            <ValueSetup
                                index={index}
                                valueOptions={value}
                                removeValue={removeSecondaryValue}
                                changeValueOptions={changeValueOptions}
                                variableOptions={variableOptions}
                                setIsIconChoserOpen={setIsIconChoserOpen}
                                setSaveIconAux={setSaveIconAux}
                            />
                        )
                    })}

                    <AddButton onClick={() => addSecondaryValue()}>
                        <i className="far fa-plus" />
                        {i18n('ADD VALUE')}
                    </AddButton>
                </Body>

                {isIconChoserOpen && (
                    <IconChooser
                        choosedIcon={getChoosedIcon()}
                        setIsIconChoserOpen={setIsIconChoserOpen}
                        saveIcon={handleSaveIcon}
                        onSave={onSave}
                    />
                )}

                <Footer>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            onSubmit()
                        }}
                    >
                        {i18n('Save')}
                    </button>
                    <button className="Cancel" onClick={onCancel}>
                        {i18n('Cancel')}
                    </button>
                </Footer>
            </StyledModal>
        ),
        [isVisible, mainValue, secondaryValues, ...secondaryValues, isIconChoserOpen, saveIconAux]
    )
}

const ValueHeader = (): JSX.Element => {
    return (
        <InfoContainer>
            <span>{i18n('Variable')}</span>
            <ValuePropsDiv>
                <span>{i18n('Label')}</span>
                <span>{i18n('Prefix')}</span>
                <span>{i18n('Sufix')}</span>
                <span>{i18n('Trend')}</span>
            </ValuePropsDiv>
            <div />
        </InfoContainer>
    )
}

const StyledModal = styled(LiveModal)`
    display: flex;

    .modal-dialog {
        width: 606px;
        height: 656px;
        margin-top: 80px;
        background: none;
    }

    .modal-content {
        border-radius: 4px;
    }
`

const Header = styled(Modal.Header)`
    display: flex;
    height: 48px;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    border: none;
    border-radius: 4px;
`

const Body = styled(Modal.Body)`
    height: 559px;
    padding: 16px;
    background-color: ${({ theme }) => theme?.['gray-200'] || '#EEEEEE'};
    border-top: 1px solid ${({ theme }) => theme?.['gray-400'] || '#c8ccd4'};
    border-bottom: 1px solid ${({ theme }) => theme?.['gray-400'] || '#c8ccd4'};

    padding-right: 0;
    overflow-y: scroll;
    overflow-x: hidden;
    ::-webkit-scrollbar {
        border-radius: 16px;
        width: 16px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 16px;
        box-shadow: inset 0 0 4px 4px ${({ theme }) => theme?.['gray-300'] || '#DDDEE1'};
        border: solid 4px transparent;
    }
`

const Footer = styled(Modal.Footer)`
    display: flex;
    height: 49px;
    align-items: center;
    border: none;
    border-radius: 4px;

    button {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        width: 79px;
        height: 32px;
        font-size: 15px;
        font-weight: 600;
        transition: 0.2s ease;
    }

    .btn-primary {
        color: ${({ theme }) => theme?.['gray-0'] || '#ffffff'};
        background-color: ${({ theme }) => theme?.['blue-500'] || '#37a3ef'};
        border: none;

        :hover {
            background-color: ${({ theme }) => theme?.['blue-700'] || '#008AC7'};
        }
    }

    .Cancel {
        color: ${({ theme }) => theme?.['gray-800'] || '#364159'};
        background-color: ${({ theme }) => theme?.['gray-0'] || '#ffffff'};
        border: 1px solid ${({ theme }) => theme?.['gray-400'] || '#c8ccd4'};

        :hover {
            filter: brightness(0.98);
        }
    }
`

const Title = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme?.['gray-700'] || '#606A80'};
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 12px;
`

export const InfoContainer = styled.div`
    padding: 0px 24px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme?.['gray-800'] || '#020202'};
    font-weight: 400;

    > span,
    div {
        margin-left: 4px;
    }

    > :nth-child(1) {
        width: 30%;
    }
    > :nth-child(2) {
        width: 55%;
    }
    > :nth-child(3) {
        width: 15%;
        display: flex;
        justify-content: flex-end;
    }
`

const DivLine = styled.div`
    width: auto;
    height: 1px;
    background-color: ${({ theme }) => theme?.['gray-400'] || '#C8CCD4'};
    margin: 24px 0;
`

const AddButton = styled.button`
    width: 100%;
    height: 22px;
    background: ${({ theme }) => theme?.['blue-300'] || '#C3E3FA'};
    border-radius: 3px;
    color: ${({ theme }) => theme?.['blue-500'] || '#37A3EF'};
    border: none;
    font-size: 14px;
    font-weight: 600;
    transition: 0.2s ease;

    i {
        padding-right: 8px;
    }

    :hover {
        color: ${({ theme }) => theme?.['blue-700'] || '#008AC7'};
        filter: brightness(0.98);
    }
`
